import { EVerticalNames } from '@constants';
import { IUserInfo, IUserSubject, IUser } from '@sdk/contracts';
import { Form, FormInstance, Select, Button, Space, InputNumber } from 'antd';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { RegionSpecificFields } from './components/RegionSpecificFields';
import {
  Wrapper,
  SubjectSelectWrapper,
  NoSpinnerInputWrapper,
  SubjectsBlock,
  TitleWrapper
} from './UniFormContent.styles';

import {
  UserInfoBranchSelector,
  UserInfoDegreeSelector,
  UserInfoUniversitySelector
} from '@/components/Form/UserInfo';
import { useRegionalization } from '@/hooks';
import { useAllCoursesForStudent } from '@/store';

type UniFormContentProps = {
  form: FormInstance<IUserInfo>;
  profile?: IUser;
  isManager: boolean;
};

export const UniFormContent: React.FC<UniFormContentProps> = ({ form, profile, isManager }) => {
  const { t } = useTranslation(['DefaultTranslations', 'StudentProfileConfigurationTranslations']);
  const { uni: uniRegionConfig } = useRegionalization();

  const { courses } = useAllCoursesForStudent(profile!);
  const userInfo = profile?.userInfo;
  const uniInfo = userInfo?.uniUserInfo;
  const [subjects, setSubjects] = useState<IUserSubject[]>(
    uniInfo?.subjects?.length
      ? uniInfo.subjects.map(s => ({ course: s.course?._id ?? '', note: s.note ?? 0 }))
      : [{ course: '', note: 0 }]
  );

  useEffect(() => {
    if (uniInfo?.subjects?.length) {
      setSubjects(uniInfo.subjects.map(s => ({ course: s.course?._id ?? '', note: s.note ?? 0 })));
    }
  }, [uniInfo]);

  useEffect(() => {
    form.setFieldsValue({
      uniUserInfo: {
        ...form.getFieldValue('uniUserInfo'),
        subjects
      }
    });
  }, [form, subjects]);

  const handleAddSubject = () => {
    setSubjects(prev => [...prev, { course: '', note: 0 }]);
  };

  const handleRemoveSubject = (idx: number) => {
    const newSubjects = subjects.filter((_, i) => i !== idx);
    setSubjects(newSubjects);
    form.setFieldsValue({
      uniUserInfo: {
        ...form.getFieldValue('uniUserInfo'),
        subjects: newSubjects
      }
    });
  };

  const handleSubjectChange = (idx: number, field: keyof IUserSubject, value: string | number) => {
    const newSubjects = [...subjects];
    newSubjects[idx][field] = value as never;
    setSubjects(newSubjects);
    form.setFieldsValue({
      uniUserInfo: {
        ...form.getFieldValue('uniUserInfo'),
        subjects: newSubjects
      }
    });
  };

  const handleBranchChange = () => {
    form.setFieldValue(['uniUserInfo', 'degree'], undefined);
    form.setFieldValue(['uniUserInfo', 'university'], undefined);
  };

  return (
    <Wrapper>
      {isManager && <TitleWrapper>{t(EVerticalNames.UNI)}</TitleWrapper>}
      <Form.Item
        name={['uniUserInfo', 'studyBranch']}
        label={t('StudentProfileConfigurationTranslations:STUDY_BRANCH')}
        rules={[
          { required: true, message: t('StudentProfileConfigurationTranslations:STUDY_BRANCH') }
        ]}>
        <UserInfoBranchSelector objName="uniUserInfo" onChange={handleBranchChange} />
      </Form.Item>

      <Form.Item
        name={['uniUserInfo', 'degree']}
        label={t('StudentProfileConfigurationTranslations:DEGREE')}
        rules={[{ required: true, message: t('StudentProfileConfigurationTranslations:DEGREE') }]}>
        <UserInfoDegreeSelector objName="uniUserInfo" />
      </Form.Item>

      <Form.Item
        name={['uniUserInfo', 'university']}
        label={t('StudentProfileConfigurationTranslations:UNIVERSITY')}
        rules={[
          { required: true, message: t('StudentProfileConfigurationTranslations:UNIVERSITY') }
        ]}>
        <UserInfoUniversitySelector objName="uniUserInfo" />
      </Form.Item>
      <RegionSpecificFields form={form} userInfo={userInfo} />
      <SubjectsBlock>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          {t('StudentProfileConfigurationTranslations:SUBJECTS_TITLE')}
        </div>
        {subjects.map((subject, idx) => (
          <Space key={idx} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
            <Form.Item
              name={['uniUserInfo', 'subjects', idx, 'course']}
              rules={[
                { required: true, message: t('StudentProfileConfigurationTranslations:SUBJECT') }
              ]}
              style={{ minWidth: 200 }}>
              <SubjectSelectWrapper>
                <Select
                  options={courses?.map(course => ({
                    label: course.name,
                    value: course._id
                  }))}
                  placeholder={t('StudentProfileConfigurationTranslations:SUBJECT')}
                  value={subject.course}
                  onChange={value => handleSubjectChange(idx, 'course', value)}
                />
              </SubjectSelectWrapper>
            </Form.Item>
            <Form.Item
              name={['uniUserInfo', 'subjects', idx, 'note']}
              rules={[
                { required: true, message: t('StudentProfileConfigurationTranslations:GRADE') }
              ]}
              style={{ width: 55 }}>
              <NoSpinnerInputWrapper>
                <InputNumber
                  min={uniRegionConfig.grade.min}
                  max={uniRegionConfig.grade.max}
                  placeholder={t('StudentProfileConfigurationTranslations:GRADE')}
                  value={subject.note}
                  onChange={value => handleSubjectChange(idx, 'note', value || 0)}
                />
              </NoSpinnerInputWrapper>
            </Form.Item>
            {subject.course && (
              <Button type="default" onClick={() => handleRemoveSubject(idx)}>
                {t('StudentProfileConfigurationTranslations:REMOVE_SUBJECT')}
              </Button>
            )}
          </Space>
        ))}
        <Button type="dashed" onClick={handleAddSubject} style={{ width: '100%' }}>
          {t('StudentProfileConfigurationTranslations:ADD_SUBJECT')}
        </Button>
      </SubjectsBlock>
    </Wrapper>
  );
};
